import type { DocBlock, DocNestedBlock } from "../blocks/blockTypes";
import { flattenDocBlocks } from "../blocks/blockFlatten";
import { DOC_VARIABLE_KEY_PATTERN } from "./variableKey";

const PLACEHOLDER_RE = /\{\{([A-Za-z][A-Za-z0-9_]*)\}\}/g;

export function substituteDocVariables(text: string, values: Record<string, string>): string {
  return text.replace(PLACEHOLDER_RE, (match, key: string) => {
    if (!DOC_VARIABLE_KEY_PATTERN.test(key)) return match;
    if (Object.prototype.hasOwnProperty.call(values, key)) return values[key] ?? "";
    return match;
  });
}

export function applyVariableValuesToBlocks(
  blocks: DocBlock[],
  values: Record<string, string>,
): DocBlock[] {
  return blocks.map((block) => applyVariableValuesToBlock(block, values));
}

function applyVariableValuesToNestedBlock(
  block: DocNestedBlock,
  values: Record<string, string>,
): DocNestedBlock {
  return applyVariableValuesToBlock(block as DocBlock, values) as DocNestedBlock;
}

function applyVariableValuesToBlock(block: DocBlock, values: Record<string, string>): DocBlock {
  switch (block.type) {
    case "heading":
      return { ...block, text: substituteDocVariables(block.text, values) };
    case "paragraph":
      return { ...block, text: substituteDocVariables(block.text, values) };
    case "rich_text":
      return { ...block, body: substituteDocVariables(block.body, values) };
    case "table":
      return {
        ...block,
        rows: block.rows.map((row) =>
          row.map((cell) => substituteDocVariables(cell, values)),
        ),
      };
    case "two_column":
      return {
        ...block,
        left: block.left.map(
          (b) => applyVariableValuesToNestedBlock(b, values),
        ),
        right: block.right.map(
          (b) => applyVariableValuesToNestedBlock(b, values),
        ),
      };
    default:
      return block;
  }
}

/** Filled value, empty fill line (null), or unresolved placeholder text. */
export function textFieldDisplayValue(
  variableKey: string,
  values: Record<string, string> | null | undefined,
): string | null {
  if (!values || !Object.prototype.hasOwnProperty.call(values, variableKey)) {
    return `{{${variableKey}}}`;
  }
  const v = values[variableKey];
  return v.trim() ? v : null;
}

export function checkboxDisplayChecked(
  block: { fillable: boolean; checked: boolean; variable_key: string },
  values: Record<string, string> | null | undefined,
): boolean {
  if (!block.fillable) return block.checked;
  if (!values || !block.variable_key) return false;
  const raw = (values[block.variable_key] ?? "").trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes" || raw === "on";
}

export { flattenDocBlocks };
